// "What's new" release notes. Bump LATEST_VERSION whenever you want the
// drawer to reappear for returning users.
//
// Entries are ordered newest-first. Each entry supports EN and ES copy.
export const LATEST_VERSION = "3.0.0";

export const RELEASES = [
  {
    version: "3.0.0",
    date: "Jun 2026",
    title_en: "New Tab: Task Cards — 46 Printable DSLR Cheat Sheets",
    title_es: "Nueva Pestana: Task Cards — 46 Hojas de Referencia DSLR Imprimibles",
    highlights_en: [
      {
        tag: "New",
        text: "A brand-new Task Cards tab: a gallery of 46 printable DSLR Advisor cheat sheets organized into 4 sets — Essentials & Basics, People/Portraiture/Genres, Creative Projects & Composition, and the Bird & Wildlife Pack.",
      },
      {
        tag: "New",
        text: "Tap any card to view it full-screen with quick settings, key tips, and a 'Remember' takeaway — then Download it as a branded PDF. Each section also has a 'Download full set' button for the whole pack.",
      },
      {
        tag: "New",
        text: "Covers everything from Aperture, ISO, the Exposure Triangle and Histogram to Portrait Posing (Male/Female/Group), Astrophotography, Panning, Bokeh, Silhouettes, Birds in Flight and Animal Behavior.",
      },
      {
        tag: "Improved",
        text: "Every card carries the DSLR Advisor branding, is fully bilingual (EN/ES), and downloads as a clean print-ready PDF.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Una nueva pestana Task Cards: una galeria de 46 hojas de referencia DSLR Advisor imprimibles organizadas en 4 sets — Esenciales y Fundamentos, Personas/Retrato/Generos, Proyectos Creativos y Composicion, y el Pack de Aves y Fauna.",
      },
      {
        tag: "Nuevo",
        text: "Toca cualquier tarjeta para verla a pantalla completa con ajustes rapidos, consejos clave y una idea para 'Recordar' — y descargala como PDF con marca. Cada seccion tiene tambien un boton 'Descargar set completo'.",
      },
      {
        tag: "Nuevo",
        text: "Cubre desde Apertura, ISO, el Triangulo de Exposicion e Histograma hasta Pose de Retrato (Hombre/Mujer/Grupo), Astrofotografia, Barrido, Bokeh, Siluetas, Aves en Vuelo y Comportamiento Animal.",
      },
      {
        tag: "Mejorado",
        text: "Cada tarjeta lleva la marca DSLR Advisor, es totalmente bilingue (EN/ES) y se descarga como un PDF listo para imprimir.",
      },
    ],
  },
  {
    version: "2.9.0",
    date: "Jun 2026",
    title_en: "New Lesson: Lighting Basics + AI Lighting Setup suggestions",
    title_es: "Nueva Leccion: Iluminacion Basica + Sugerencias de Iluminacion con IA",
    highlights_en: [
      {
        tag: "New",
        text: "Lighting Basics (Learn → Lighting Basics) — the foundational lesson before Lighting Techniques. Covers three-point lighting (key/fill/rim) with a diagram, hard vs soft light, direction of light, the inverse-square law, and color temperature / white balance.",
      },
      {
        tag: "New",
        text: "AI 'Describe Your Shot' now suggests a Lighting Setup (softbox, umbrella, reflector, ring light, etc.) for indoor/studio/low-light shots — even if you don't own the gear — so you learn what lighting the shot needs. It auto-hides for bright outdoor daylight.",
      },
      {
        tag: "Improved",
        text: "The AI Lighting Setup card links straight to the new Lighting Basics lesson. The Learn menu now lists 'Lighting Basics' before 'Lighting Techniques'. Fully bilingual (EN/ES) with PDF download.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Iluminacion Basica (Aprender → Iluminacion Basica) — la leccion fundamental antes de Tecnicas de Iluminacion. Cubre la iluminacion de tres puntos (principal/relleno/borde) con diagrama, luz dura vs suave, direccion de la luz, la ley del cuadrado inverso y temperatura de color / balance de blancos.",
      },
      {
        tag: "Nuevo",
        text: "La IA 'Describe tu Toma' ahora sugiere un Equipo de Iluminacion (softbox, paraguas, reflector, ring light, etc.) para tomas en interior/estudio/poca luz — aunque no tengas el equipo — para que aprendas que iluminacion necesita la toma. Se oculta solo en luz exterior brillante.",
      },
      {
        tag: "Mejorado",
        text: "La tarjeta de Equipo de Iluminacion de la IA enlaza directo a la nueva leccion de Iluminacion Basica. El menu Aprender ahora lista 'Iluminacion Basica' antes de 'Tecnicas de Iluminacion'. Totalmente bilingue (EN/ES) con descarga en PDF.",
      },
    ],
  },
  {
    version: "2.8.0",
    date: "Jun 2026",
    title_en: "Curriculum Complete: Camera Anatomy, The Bayer Filter & History of the Camera",
    title_es: "Curriculum Completo: Anatomia de la Camara, El Filtro Bayer e Historia de la Camara",
    highlights_en: [
      {
        tag: "New",
        text: "The Anatomy of the DSLR & Mirrorless (Learn → Camera Anatomy) — labeled SVG cross-section diagrams of the DSLR vs mirrorless light path, plus all 10 core components: lens & mount, aperture diaphragm, mirror/pentaprism, shutter (mechanical/electronic/global), sensor, processor, IBIS, OVF vs EVF, controls, and card slots. Includes an honest DSLR-vs-mirrorless comparison table.",
      },
      {
        tag: "New",
        text: "The Bayer Filter — How Cameras See Color (Learn → The Bayer Filter) — the most overlooked concept in digital photography. Why sensors are colorblind, the RGGB color filter array, why there's twice as much green, demosaicing step-by-step, why RAW is so powerful, and the X-Trans / Foveon / monochrome alternatives. With a custom Bayer-pattern diagram.",
      },
      {
        tag: "New",
        text: "A Brief History of the Camera (Learn → History of the Camera) — a visual vertical timeline from the camera obscura (~1000 AD) → daguerreotype → Kodak roll film → 35mm Leica → SLR → autofocus → digital/DSLR → mirrorless → computational & smartphone photography → the future (global shutter & AI).",
      },
      {
        tag: "Improved",
        text: "All three lessons are fully bilingual (EN/ES) with downloadable PDFs and cross-links. The Learn dropdown now opens with these three foundational lessons. User Guide and Presenter Script updated.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "La Anatomia de la DSLR y Mirrorless (Aprender → Anatomia de la Camara) — diagramas SVG de corte transversal del recorrido de la luz en DSLR vs mirrorless, mas los 10 componentes principales: lente y montura, diafragma, espejo/pentaprisma, obturador (mecanico/electronico/global), sensor, procesador, IBIS, OVF vs EVF, controles y ranuras de tarjeta. Incluye una tabla comparativa honesta DSLR vs mirrorless.",
      },
      {
        tag: "Nuevo",
        text: "El Filtro Bayer — Como las Camaras Ven el Color (Aprender → El Filtro Bayer) — el concepto mas ignorado de la fotografia digital. Por que los sensores son daltonicos, el arreglo de filtros RGGB, por que hay el doble de verde, el demosaicing paso a paso, por que el RAW es tan potente, y las alternativas X-Trans / Foveon / monocromaticas. Con un diagrama del patron Bayer.",
      },
      {
        tag: "Nuevo",
        text: "Una Breve Historia de la Camara (Aprender → Historia de la Camara) — una linea de tiempo vertical visual desde la camara oscura (~1000 d.C.) → daguerrotipo → rollo Kodak → Leica 35mm → SLR → autoenfoque → digital/DSLR → mirrorless → fotografia computacional y de smartphone → el futuro (obturador global e IA).",
      },
      {
        tag: "Mejorado",
        text: "Las tres lecciones son totalmente bilingues (EN/ES) con PDF descargables y enlaces cruzados. El menu Aprender ahora abre con estas tres lecciones fundamentales. Manual de Usuario y Script de Presentador actualizados.",
      },
    ],
  },
  {
    version: "2.7.0",
    date: "Jun 2026",
    title_en: "New: AI 'Describe Your Shot' Mode on the Calculator",
    title_es: "Nuevo: Modo IA 'Describe tu Toma' en la Calculadora",
    highlights_en: [
      {
        tag: "New",
        text: "The Advisor calculator now has a Manual / Describe Your Shot toggle. Switch to AI mode and type the photo you want to take in plain language (e.g. 'full moon at night with my Tamron 70-300mm on a tripod').",
      },
      {
        tag: "New",
        text: "The AI returns a strong starting point: Aperture, Shutter Speed, ISO, a one-line scenario summary, and 2-4 pro tips written specifically for that shot.",
      },
      {
        tag: "New",
        text: "Gear-aware: if you've saved your kit in My Gear, the AI recommends the best lens you actually own for the shot (badged 'From your gear'); otherwise it suggests an ideal lens type.",
      },
      {
        tag: "New",
        text: "'Apply to Manual Form' button — the AI fills the full Manual calculator with the inferred scene so you can fine-tune every control and save it to your History.",
      },
      {
        tag: "Improved",
        text: "Fully bilingual (EN/ES). The Manual calculator is unchanged — AI mode sits right beside it. User Guide and Presenter Script updated with the new feature.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "La calculadora del Asesor ahora tiene un interruptor Manual / Describe tu Toma. Cambia al modo IA y escribe la foto que quieres tomar en lenguaje sencillo (ej. 'luna llena de noche con mi Tamron 70-300mm en tripode').",
      },
      {
        tag: "Nuevo",
        text: "La IA devuelve un punto de partida solido: Apertura, Velocidad de Obturacion, ISO, un resumen del escenario en una linea, y 2-4 consejos pro escritos especificamente para esa toma.",
      },
      {
        tag: "Nuevo",
        text: "Consciente de tu equipo: si guardaste tu kit en Mi Equipo, la IA recomienda el mejor objetivo que realmente tienes para la toma (con insignia 'De tu equipo'); de lo contrario sugiere un tipo de objetivo ideal.",
      },
      {
        tag: "Nuevo",
        text: "Boton 'Aplicar al Formulario Manual' — la IA rellena la calculadora Manual completa con la escena inferida para que ajustes cada control y lo guardes en tu Historial.",
      },
      {
        tag: "Mejorado",
        text: "Totalmente bilingue (EN/ES). La calculadora Manual no cambia — el modo IA esta justo al lado. Manual de Usuario y Script de Presentador actualizados con la nueva funcion.",
      },
    ],
  },
  {
    version: "2.6.0",
    date: "Feb 2026",
    title_en: "New Lesson: Posing Guide — 20 Essential Portrait Poses + Galeria Social Card",
    title_es: "Nueva Leccion: Guia de Poses — 20 Poses Esenciales + Tarjeta Galeria Social",
    highlights_en: [
      {
        tag: "New",
        text: "Posing Guide page (Learn → Posing Guide) — 20 essential portrait poses across 6 categories: 5 Standing, 4 Seated, 3 Close-Up, 3 Couples, 3 Group, 2 Creative / Editorial.",
      },
      {
        tag: "New",
        text: "Each pose card shows a side-by-side AI-generated reference photo + stick-figure SVG diagram, when to use it, a 5-step setup, photographer angle + recommended lens, common mistakes (red X marks), and lighting + outfit suggestion.",
      },
      {
        tag: "New",
        text: "Standing classics: Contrapposto, S-Curve, Hands-in-Pockets, Lean Against Wall, Walking Step. Seated: Cross-Legged, Knees-to-Chest, Stairs, Reclining. Close-Up: Chin-on-Hand, Over Shoulder, Hands Framing Face. Couples: Forehead-to-Forehead, Hand-in-Hand, Back-to-Back. Group: Triangle, V Formation, Linear Depths. Creative: Silhouette, Hands-Only.",
      },
      {
        tag: "New",
        text: "Quick-jump category sub-nav, difficulty badges (Beginner / Intermediate / Advanced), and a downloadable PDF printable for classroom or on-set field-guide use.",
      },
      {
        tag: "New",
        text: "Galeria Social cross-promo card added to the home page — QR code + link to galeriasocial.com so users can discover the photography gallery + social platform from the same author.",
      },
      {
        tag: "Improved",
        text: "User Guide and Presenter Script updated (EN+ES) with the Posing Guide section. PART 11 runtime now 41 min.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Pagina Guia de Poses (Aprender → Guia de Poses) — 20 poses esenciales de retrato en 6 categorias: 5 De Pie, 4 Sentado/a, 3 Primer Plano, 3 Parejas, 3 Grupos, 2 Creativo / Editorial.",
      },
      {
        tag: "Nuevo",
        text: "Cada tarjeta muestra lado a lado: foto de referencia IA + diagrama de figura SVG, cuando usarla, configuracion de 5 pasos, angulo del fotografo + lente recomendado, errores comunes (X rojas), y sugerencia de iluminacion + vestuario.",
      },
      {
        tag: "Nuevo",
        text: "Clasicas de Pie: Contrapposto, Curva en S, Manos en Bolsillos, Apoyado en Pared, Paso Caminando. Sentado/a: Piernas Cruzadas, Rodillas al Pecho, Escaleras, Reclinado. Primer Plano: Menton en Mano, Sobre el Hombro, Manos Enmarcando. Parejas: Frente con Frente, De la Mano, Espalda con Espalda. Grupos: Triangulo, Formacion V, Lineales con Profundidad. Creativo: Silueta, Solo Manos.",
      },
      {
        tag: "Nuevo",
        text: "Sub-nav de salto rapido por categoria, insignias de dificultad (Principiante / Intermedio / Avanzado), y PDF descargable imprimible para aula o uso como guia de campo en set.",
      },
      {
        tag: "Nuevo",
        text: "Tarjeta de cross-promo Galeria Social añadida a la pagina de inicio — codigo QR + enlace a galeriasocial.com para que los usuarios descubran la galeria fotografica + plataforma social del mismo autor.",
      },
      {
        tag: "Mejorado",
        text: "Manual de Usuario y Script de Presentador actualizados (EN+ES) con la seccion de Guia de Poses. PARTE 11 ahora dura 41 min.",
      },
    ],
  },
  {
    version: "2.5.0",
    date: "Feb 2026",
    title_en: "New: 'Ready to Upgrade?' Card — From Phone to DSLR Progression",
    title_es: "Nuevo: Tarjeta 'Listo para Mejorar?' — Progresion de Telefono a DSLR",
    highlights_en: [
      {
        tag: "New",
        text: "'Ready to Upgrade?' card at the bottom of the Smartphone Photography page — guides phone-only users through the natural progression to a dedicated DSLR or mirrorless camera.",
      },
      {
        tag: "New",
        text: "5 honest scenarios where a real camera dramatically outperforms even the best phone: extreme low light / astrophotography, wildlife & sports telephoto, large fine-art prints, true f/1.4 bokeh, and high-FPS sports tracking.",
      },
      {
        tag: "New",
        text: "4-step upgrade path with direct in-app links: open the DSLR Settings Advisor, read the Careers 'Camera Body Decision' lesson, plan your kit in My Gear, and explore the Shots Library.",
      },
      {
        tag: "Improved",
        text: "Closes the educational loop — students fall in love with photography on their phone, then have a clear, friendly path to upgrade when their eye outgrows the device.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Tarjeta 'Listo para Mejorar?' al final de la pagina de Fotografia con Smartphone — guia a usuarios de solo-telefono por la progresion natural a una DSLR o mirrorless dedicada.",
      },
      {
        tag: "Nuevo",
        text: "5 escenarios honestos donde una camara real supera dramaticamente al mejor telefono: baja luz extrema / astrofotografia, telefoto de fauna y deportes, impresiones grandes de bellas artes, bokeh f/1.4 real, y rastreo deportivo de alta FPS.",
      },
      {
        tag: "Nuevo",
        text: "Ruta de upgrade de 4 pasos con enlaces directos en la app: abrir el Asesor de Ajustes DSLR, leer la leccion de 'Decision de Cuerpo de Camara' en Carreras, planear tu kit en Mi Equipo, y explorar la Biblioteca de Planos.",
      },
      {
        tag: "Mejorado",
        text: "Cierra el loop educativo — los estudiantes se enamoran de la fotografia con su telefono, luego tienen una ruta clara y amigable para mejorar cuando su ojo supere al dispositivo.",
      },
    ],
  },
  {
    version: "2.4.0",
    date: "Feb 2026",
    title_en: "Smartphone Photography — 10 Practice Projects with Self-Critique",
    title_es: "Fotografia con Smartphone — 10 Proyectos de Practica con Auto-Critica",
    highlights_en: [
      {
        tag: "New",
        text: "10 hands-on practice projects added to the Smartphone Photography page — students can now apply each lesson with concrete shooting assignments.",
      },
      {
        tag: "New",
        text: "Projects cover: Food (window light), 3-Style Portraits, Golden Hour Landscape with ultra-wide + RAW, Candid Street, Low-Light/Night Mode, Macro, E-Commerce Product, Action/Sports burst, Creative Self-Portrait series, and a culminating '24 Hours in My Life' visual story.",
      },
      {
        tag: "New",
        text: "Every project includes a 5-question SELF-CRITIQUE CHECKLIST so students (or a friend) can honestly assess their work — the educational missing-link between shooting and improving.",
      },
      {
        tag: "New",
        text: "Each project shows difficulty badge (Beginner / Intermediate / Advanced), the lessons it builds on, starting settings, 5 specific tips, and required deliverables.",
      },
      {
        tag: "Improved",
        text: "Quick-jump sub-nav at the top of the page now includes a prominent 'Projects' link. PDF download now includes the full project workbook (printable for offline classroom use).",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "10 proyectos practicos añadidos a la pagina de Fotografia con Smartphone — los estudiantes ya pueden aplicar cada leccion con asignaciones concretas de disparo.",
      },
      {
        tag: "Nuevo",
        text: "Los proyectos cubren: Comida (luz de ventana), Retratos en 3 estilos, Paisaje de Golden Hour con ultra-gran-angular + RAW, Callejera Candida, Baja Luz/Modo Noche, Macro, Producto E-Commerce, Accion/Deportes en rafaga, Serie Creativa de Auto-Retratos, y un proyecto culminante '24 Horas en Mi Vida'.",
      },
      {
        tag: "Nuevo",
        text: "Cada proyecto incluye una LISTA DE AUTO-CRITICA de 5 preguntas para que los estudiantes (o un amigo) evaluen honestamente su trabajo — el eslabon educativo perdido entre disparar y mejorar.",
      },
      {
        tag: "Nuevo",
        text: "Cada proyecto muestra una insignia de dificultad (Principiante / Intermedio / Avanzado), las lecciones en las que se apoya, ajustes iniciales, 5 tips especificos, y entregables requeridos.",
      },
      {
        tag: "Mejorado",
        text: "El sub-nav de salto rapido arriba de la pagina ahora incluye un enlace prominente a 'Proyectos'. La descarga PDF incluye el workbook completo de proyectos (imprimible para uso de aula sin conexion).",
      },
    ],
  },
  {
    version: "2.3.0",
    date: "Feb 2026",
    title_en: "New Lesson: Smartphone Photography — iOS + Android Side-by-Side",
    title_es: "Nueva Leccion: Fotografia con Smartphone — iOS + Android Lado a Lado",
    highlights_en: [
      {
        tag: "New",
        text: "Smartphone Photography page (Learn → Smartphone Photography) — 6 mobile-photography lessons, each card showing iOS and Android steps side-by-side.",
      },
      {
        tag: "New",
        text: "Lesson 1: Mastering Mobile Exposure & Focus — tap-to-focus + sun-slider exposure on iOS, brightness slider on Android, AE/AF Lock for recomposing without re-metering.",
      },
      {
        tag: "New",
        text: "Lesson 2: Pro Mode / Manual Controls — third-party app recommendations for iOS (Halide, Lightroom Mobile camera) and built-in Pro mode walkthroughs for Samsung, Pixel, and OnePlus.",
      },
      {
        tag: "New",
        text: "Lesson 3: Composition Rules — exact paths to enable 3x3 Grid Lines on iOS Settings and stock Android cameras for instant Rule of Thirds.",
      },
      {
        tag: "New",
        text: "Lesson 4: Lens Selection — when to use .5x ultra-wide, 1x main, and 2x-5x telephoto on iPhone Pro and Samsung Ultra / Pixel Pro. Why digital zoom (pinch beyond longest lens) is forbidden.",
      },
      {
        tag: "New",
        text: "Lesson 5: Portrait Mode & Artificial Depth of Field — proper 2-8 ft distance, iOS lighting styles, retroactive f-stop adjustment, Pixel's depth AI for pets and objects.",
      },
      {
        tag: "New",
        text: "Lesson 6: Mobile Editing & RAW Capture — enabling Apple ProRAW on iPhone 12 Pro+, RAW (.DNG) on Samsung/Pixel/OnePlus, plus quick-edit workflows for Lightroom Mobile and Snapseed.",
      },
      {
        tag: "Improved",
        text: "User Guide and Presenter Script updated (EN+ES) with the new Smartphone Photography section. PART 11 runtime now 37 min.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Pagina de Fotografia con Smartphone (Aprender → Fotografia con Smartphone) — 6 lecciones de fotografia movil, cada tarjeta muestra pasos iOS y Android lado a lado.",
      },
      {
        tag: "Nuevo",
        text: "Leccion 1: Dominar la Exposicion y el Enfoque Movil — tap-para-enfocar + slider de sol en iOS, slider de brillo en Android, bloqueo AE/AF para recomponer sin remedir.",
      },
      {
        tag: "Nuevo",
        text: "Leccion 2: Modo Pro / Controles Manuales — recomendaciones de apps de terceros para iOS (Halide, camara de Lightroom Mobile) y recorridos del modo Pro integrado para Samsung, Pixel y OnePlus.",
      },
      {
        tag: "Nuevo",
        text: "Leccion 3: Reglas de Composicion — rutas exactas para activar lineas de cuadricula 3x3 en Ajustes de iOS y camaras stock Android para Regla de Tercios instantanea.",
      },
      {
        tag: "Nuevo",
        text: "Leccion 4: Seleccion de Lentes — cuando usar .5x ultra-gran-angular, 1x principal, y 2x-5x telefoto en iPhone Pro y Samsung Ultra / Pixel Pro. Por que el zoom digital (pellizcar mas alla del lente mas largo) esta prohibido.",
      },
      {
        tag: "Nuevo",
        text: "Leccion 5: Modo Retrato y Profundidad de Campo Artificial — distancia adecuada 60cm-2.4m, estilos de iluminacion iOS, ajuste retroactivo de apertura, IA de profundidad de Pixel para mascotas y objetos.",
      },
      {
        tag: "Nuevo",
        text: "Leccion 6: Edicion Movil y Captura RAW — activar Apple ProRAW en iPhone 12 Pro+, RAW (.DNG) en Samsung/Pixel/OnePlus, mas flujos rapidos de edicion en Lightroom Mobile y Snapseed.",
      },
      {
        tag: "Mejorado",
        text: "Manual de Usuario y Script de Presentador actualizados (EN+ES) con la nueva seccion de Fotografia con Smartphone. PARTE 11 ahora dura 37 min.",
      },
    ],
  },
  {
    version: "2.2.0",
    date: "Feb 2026",
    title_en: "New Lesson: The Gray Card — Color Accuracy & Exposure Precision",
    title_es: "Nueva Leccion: La Tarjeta Gris — Precision de Color y Exposicion",
    highlights_en: [
      {
        tag: "New",
        text: "The Gray Card lesson (Learn → The Gray Card) — the cheapest, most under-used tool in professional photography, taught with a visual 18% middle-gray reference between pure black and pure white.",
      },
      {
        tag: "New",
        text: "Overcoming the Internal Meter Trap — 4 worked examples (bride in white, black cat, snowy landscape, backlit silhouette) showing exactly how camera meters fail and how the gray card fixes each one. Includes a 5-step Spot Metering + AE-Lock workflow.",
      },
      {
        tag: "New",
        text: "Custom White Balance Mastery — 6-step in-camera procedure for setting Custom WB from a card frame, plus genre-specific guidance for product, portrait, lifestyle, and real-estate photography.",
      },
      {
        tag: "New",
        text: "Multi-Camera Color Synchronization — 6-step Lightroom / Capture One workflow for bridging color between a DSLR, smartphone, and drone using a single reference card frame per camera. Built for modern 2026 hybrid shoots.",
      },
      {
        tag: "New",
        text: "High-Gloss & Reflective Surface Management — using the card to detect 'hot spots' and color spill on jewelry, glass, chrome, and lacquered cosmetics packaging.",
      },
      {
        tag: "New",
        text: "Focus & Sharpness Verification — using the card's high-contrast markings + brand-specific AF micro-adjustment (Canon AFMA, Nikon AF Fine Tune, Sony AF Micro Adj) to calibrate tack-sharp focus at f/8-f/11.",
      },
      {
        tag: "Improved",
        text: "User Guide and Presenter Script updated (EN+ES) with the new Gray Card section. PART 11 runtime now 33 min.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Leccion La Tarjeta Gris (Aprender → La Tarjeta Gris) — la herramienta mas barata y subutilizada en fotografia profesional, enseñada con una referencia visual de gris 18% entre negro puro y blanco puro.",
      },
      {
        tag: "Nuevo",
        text: "Vencer la Trampa del Fotometro — 4 ejemplos trabajados (novia de blanco, gato negro, paisaje nevado, silueta a contraluz) mostrando exactamente como fallan los fotometros y como la tarjeta gris arregla cada caso. Incluye flujo de 5 pasos de Medicion Puntual + AE-Lock.",
      },
      {
        tag: "Nuevo",
        text: "Maestria en Balance de Blancos Custom — procedimiento de 6 pasos en camara para configurar WB Custom desde un frame de tarjeta, mas guia por genero para fotografia de producto, retrato, lifestyle y bienes raices.",
      },
      {
        tag: "Nuevo",
        text: "Sincronizacion de Color Multi-Camara — flujo de 6 pasos en Lightroom / Capture One para puentear color entre DSLR, smartphone y dron usando un solo frame de referencia por camara. Hecho para sesiones hibridas modernas 2026.",
      },
      {
        tag: "Nuevo",
        text: "Manejo de Superficies Brillantes y Reflectantes — usando la tarjeta para detectar 'hot spots' y derrame de color en joyeria, vidrio, cromados y empaques laqueados de cosmeticos.",
      },
      {
        tag: "Nuevo",
        text: "Verificacion de Enfoque y Nitidez — usando las marcas de alto contraste de la tarjeta + micro-ajuste de AF por marca (Canon AFMA, Nikon AF Fine Tune, Sony AF Micro Adj) para calibrar enfoque navaja-nitido a f/8-f/11.",
      },
      {
        tag: "Mejorado",
        text: "Manual de Usuario y Script de Presentador actualizados (EN+ES) con la nueva seccion de Tarjeta Gris. PARTE 11 ahora dura 33 min.",
      },
    ],
  },
  {
    version: "2.1.0",
    date: "Feb 2026",
    title_en: "New: Photography Contracts & Model Release Forms (Bilingual, Printable PDFs)",
    title_es: "Nuevo: Contratos Fotograficos y Formularios de Cesion de Imagen (Bilingues, PDF Imprimibles)",
    highlights_en: [
      {
        tag: "New",
        text: "Contracts & Forms page (Learn → Contracts & Forms) — two professional-grade legal templates every working photographer needs, downloadable and printable.",
      },
      {
        tag: "New",
        text: "Photography Services Agreement — a 12-section sample contract: parties, scope, deliverables, fees, cancellation tiers (30+ days / 8-30 / 0-7), copyright & licensing (with 2026 AI-training opt-out clause), editing scope, liability limits, backup retention, governing law, and signatures.",
      },
      {
        tag: "New",
        text: "Model Release Form — a 9-section release granting commercial rights to use a subject's likeness. Includes a dedicated MINOR section (parent/guardian signature + government ID), waiver of inspection rights, optional restricted-uses clause, and a witness signature line.",
      },
      {
        tag: "Fixed",
        text: "Crop Factor sensor-comparison diagram — labels no longer overlap. Names and crop factors now live in a clean 2-column legend below the diagram for full readability.",
      },
      {
        tag: "Improved",
        text: "User Guide and Presenter Script updated (EN+ES) with the new Contracts & Forms section. PART 11 runtime expanded to 30 min.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Pagina de Contratos y Formularios (Aprender → Contratos y Formularios) — dos plantillas legales de grado profesional que todo fotografo necesita, descargables e imprimibles.",
      },
      {
        tag: "Nuevo",
        text: "Acuerdo de Servicios Fotograficos — contrato de muestra de 12 secciones: partes, alcance, entregables, honorarios, niveles de cancelacion (30+ dias / 8-30 / 0-7), derechos de autor y licencia (con clausula 2026 de opt-out de entrenamiento de IA), alcance de edicion, limites de responsabilidad, retencion de respaldos, ley aplicable y firmas.",
      },
      {
        tag: "Nuevo",
        text: "Formulario de Cesion de Imagen (Model Release) — cesion de 9 secciones que otorga derechos comerciales para usar la imagen del sujeto. Incluye seccion dedicada a MENORES DE EDAD (firma de padre/tutor + ID oficial), renuncia al derecho de inspeccion, clausula opcional de usos restringidos y linea para firma de testigo.",
      },
      {
        tag: "Arreglado",
        text: "Diagrama de comparacion de sensores (Factor de Recorte) — las etiquetas ya no se sobreponen. Los nombres y factores de recorte ahora viven en una leyenda limpia de 2 columnas debajo del diagrama, completamente legible.",
      },
      {
        tag: "Mejorado",
        text: "Manual de Usuario y Script de Presentador actualizados (EN+ES) con la nueva seccion de Contratos y Formularios. PARTE 11 ahora dura 30 min.",
      },
    ],
  },
  {
    version: "2.0.0",
    date: "Feb 2026",
    title_en: "Curriculum Complete — Filters, Compression, Focus, WB/DR, Maintenance + 5 New Quiz Questions",
    title_es: "Curriculum Completo — Filtros, Compresion, Enfoque, BB/RD, Mantenimiento + 5 Preguntas Nuevas",
    highlights_en: [
      {
        tag: "New",
        text: "Hardware Filters lesson (UV / Polarizer / ND) with TWO interactive Before/After draggable sliders that show the dramatic effect of a Circular Polarizer (sky depth + water reflections) and a 10-stop ND filter (frozen waterfall vs silky long exposure).",
      },
      {
        tag: "New",
        text: "Lens Compression lesson — the most-misunderstood concept in photography, taught with a Before/After slider proving that the LENS does not compress: 24mm at 1m vs 200mm at 30m, same subject, totally different mountain scale. The photographer's FEET create the perspective.",
      },
      {
        tag: "New",
        text: "Advanced Focus lesson — complete brand-specific Back-Button Focus (BBF) setup guides for Canon, Nikon Z, Sony A7/A1, and Fujifilm + AI Eye-AF configuration walkthroughs (Subject Detection: Human / Animal / Bird / Vehicle).",
      },
      {
        tag: "New",
        text: "White Balance & Dynamic Range lesson — interactive Kelvin gradient strip with 8 named presets, the 'golden rule' (LOW K for warm light, HIGH K for cool light), 5-step DR home-test procedure, and bracketing/HDR workflow. Explicit cross-reference to the Histogram lesson.",
      },
      {
        tag: "New",
        text: "Camera Maintenance lesson — PROHIBITED tools (canned air, cotton swabs, your breath), safe tools in escalating order (rocket blower → brush → wet swab), step-by-step mirrorless sensor cleaning, and weather-protection protocols for 7 conditions (rain / snow / beach / desert / cold / heat).",
      },
      {
        tag: "Improved",
        text: "Photography Knowledge Quiz expanded from 12 to 17 questions — new technical scenarios: spot metering a white bird, decoupling AF, canned-air sensor dangers, +EV for snow, and the lens compression truth.",
      },
      {
        tag: "Improved",
        text: "Learn dropdown now has 18 lessons. User Guide and Presenter Script (EN+ES) updated with all 5 new lessons; PART 11 runtime expanded to 28 min.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Leccion de Filtros de Hardware (UV / Polarizador / ND) con DOS sliders interactivos Antes/Despues arrastrables que muestran el efecto dramatico de un Polarizador Circular (profundidad de cielo + reflejos en agua) y un filtro ND de 10 pasos (cascada congelada vs exposicion larga sedosa).",
      },
      {
        tag: "Nuevo",
        text: "Leccion de Compresion de Lente — el concepto mas malentendido en fotografia, enseñado con un slider Antes/Despues que prueba que el LENTE no comprime: 24mm a 1m vs 200mm a 30m, mismo sujeto, escala de montañas totalmente distinta. Los PIES del fotografo crean la perspectiva.",
      },
      {
        tag: "Nuevo",
        text: "Leccion de Enfoque Avanzado — guias de configuracion de Back-Button Focus (BBF) especificas por marca para Canon, Nikon Z, Sony A7/A1 y Fujifilm + recorridos de configuracion de AI Eye-AF (Subject Detection: Humano / Animal / Ave / Vehiculo).",
      },
      {
        tag: "Nuevo",
        text: "Leccion de Balance de Blancos y Rango Dinamico — franja de gradiente Kelvin interactiva con 8 presets, la 'regla de oro' (K BAJO para luz CALIDA, K ALTO para luz FRIA), procedimiento de prueba de RD en casa en 5 pasos, y flujo de bracketing/HDR. Referencia cruzada explicita a la leccion del Histograma.",
      },
      {
        tag: "Nuevo",
        text: "Leccion de Mantenimiento de Camara — herramientas PROHIBIDAS (aire en lata, hisopos de algodon, tu aliento), herramientas seguras en orden ascendente (soplador → cepillo → hisopo humedo), procedimiento paso a paso de limpieza de sensor mirrorless, y protocolos de proteccion climatica para 7 condiciones (lluvia / nieve / playa / desierto / frio / calor).",
      },
      {
        tag: "Mejorado",
        text: "Quiz de Conocimiento Fotografico expandido de 12 a 17 preguntas — nuevos escenarios tecnicos: medicion puntual a un ave blanca, desacoplar el AF, peligros del aire en lata para el sensor, +EV para nieve, y la verdad sobre la compresion de lente.",
      },
      {
        tag: "Mejorado",
        text: "El menu Aprender ahora tiene 18 lecciones. Manual de Usuario y Script de Presentador (EN+ES) actualizados con las 5 lecciones nuevas; PARTE 11 ahora dura 28 min.",
      },
    ],
  },
  {
    version: "1.9.0",
    date: "May 2026",
    title_en: "Exposure Foundations — Metering, Compensation, Crop Factor",
    title_es: "Fundamentos de Exposicion — Medicion, Compensacion, Factor de Recorte",
    highlights_en: [
      {
        tag: "New",
        text: "Metering Modes lesson — covers Matrix/Evaluative/Multi-segment, Center-Weighted, Partial, and Spot metering across Canon/Nikon/Sony/Fuji with SVG diagrams showing where each mode 'looks' in the frame, brand button paths, and a pro workflow that links meter → histogram → compensation.",
      },
      {
        tag: "New",
        text: "Exposure Compensation lesson with an INTERACTIVE SLIDER (−3 to +3 EV) that applies a live brightness filter to a sample photo + shifts a small histogram in real time. Includes the 18%-gray problem explanation, 8 real-world scenario quick-reference (snow / backlit portraits / sunset / dark stage / etc.), brand button locations, and which auto modes accept EV adjustments.",
      },
      {
        tag: "New",
        text: "Focal Length & Crop Factor lesson — visual sensor-size comparison diagram (Medium Format → Full Frame → APS-C Canon/Nikon → Micro 4/3 → 1-inch all drawn to scale), crop-factor multiplication examples, lens-designation map (RF/RF-S, FX/DX, FE/E, XF/GF), and 'which sensor for which photographer' guide.",
      },
      {
        tag: "Improved",
        text: "Curriculum reordered: Histogram → Metering → Exposure Compensation → Crop Factor → Shots groups all fundamentals before applied techniques. Learn dropdown now has 13 lessons.",
      },
      {
        tag: "Improved",
        text: "All new lessons explicitly cross-reference The Histogram lesson — students learn to use the histogram + Blinkies as feedback for choosing metering mode and dialing exposure compensation.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Leccion de Modos de Medicion — cubre Matrix/Evaluativa/Multi-segmento, Promedio Central, Parcial y Puntual (Spot) en Canon/Nikon/Sony/Fuji con diagramas SVG que muestran donde 'mira' cada modo en el encuadre, rutas de boton por marca, y un flujo profesional que conecta medidor → histograma → compensacion.",
      },
      {
        tag: "Nuevo",
        text: "Leccion de Compensacion de Exposicion con SLIDER INTERACTIVO (−3 a +3 EV) que aplica un filtro de brillo en vivo a una foto de ejemplo + desplaza un mini histograma en tiempo real. Incluye explicacion del problema del 18% gris, guia rapida de 8 escenarios reales (nieve / retratos a contraluz / atardecer / escenario oscuro / etc.), ubicacion de botones por marca y en que modos automaticos funciona.",
      },
      {
        tag: "Nuevo",
        text: "Leccion de Distancia Focal y Factor de Recorte — diagrama comparativo visual de tamaños de sensor (Medium Format → Full Frame → APS-C Canon/Nikon → Micro 4/3 → 1-pulgada a escala), ejemplos de multiplicacion del factor de recorte, mapa de denominaciones de lentes (RF/RF-S, FX/DX, FE/E, XF/GF), y guia 'que sensor para que fotografo'.",
      },
      {
        tag: "Mejorado",
        text: "Curriculum reorganizado: Histograma → Medicion → Compensacion → Factor de Recorte → Planos agrupa los fundamentos antes de las tecnicas aplicadas. El menu Aprender ahora tiene 13 lecciones.",
      },
      {
        tag: "Mejorado",
        text: "Todas las lecciones nuevas referencian explicitamente la leccion del Histograma — los estudiantes aprenden a usar el histograma + Blinkies como feedback para elegir modo de medicion y aplicar compensacion de exposicion.",
      },
    ],
  },
  {
    version: "1.8.0",
    date: "May 2026",
    title_en: "The Histogram — Your Guide to Perfect Exposure",
    title_es: "El Histograma — Tu Guia para la Exposicion Perfecta",
    highlights_en: [
      {
        tag: "New",
        text: "Histogram lesson under Learn → The Histogram: brand-agnostic guide that works for Canon, Nikon, Fujifilm, and Sony users.",
      },
      {
        tag: "New",
        text: "Interactive Tonal Map showing the 5 zones (Black Point, Shadows, Midtones, Highlights, White Point) on a gradient strip.",
      },
      {
        tag: "New",
        text: "Color-coded Luminance and RGB histogram diagrams for 3 scene types: Low Key (dark alley), High Key (snowy landscape), High Contrast (sunset silhouette) — each with real AI-generated photo + matching histogram side-by-side.",
      },
      {
        tag: "New",
        text: "Highlighted 'The Wall' clipping alert section explaining why edge-touching data is permanently lost in editing.",
      },
      {
        tag: "New",
        text: "Universal Hardware Guide: the DISP/INFO button logic for Canon, Nikon, Sony, and Fujifilm — plus a dedicated 'Blinkies' (Highlight Alert) section with brand-specific menu paths.",
      },
      {
        tag: "Improved",
        text: "Emphasis on 'no perfect bell-curve' — the histogram must match the mood of the scene, not an abstract ideal.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Leccion del Histograma en Aprender → El Histograma: guia agnostica de marca que funciona para usuarios de Canon, Nikon, Fujifilm y Sony.",
      },
      {
        tag: "Nuevo",
        text: "Mapa Tonal interactivo mostrando las 5 zonas (Punto Negro, Sombras, Medios Tonos, Altas Luces, Punto Blanco) sobre una franja de gradiente.",
      },
      {
        tag: "Nuevo",
        text: "Diagramas de histograma Luminancia y RGB con codigo de color para 3 tipos de escena: Low Key (callejon oscuro), High Key (paisaje nevado), Alto Contraste (silueta al atardecer) — cada uno con foto IA real + histograma lado a lado.",
      },
      {
        tag: "Nuevo",
        text: "Seccion destacada 'El Muro' sobre el recorte (clipping), explicando por que los datos que tocan el borde se pierden de forma permanente al editar.",
      },
      {
        tag: "Nuevo",
        text: "Guia Universal de Hardware: la logica del boton DISP/INFO para Canon, Nikon, Sony y Fujifilm — mas una seccion dedicada a 'Los Blinkies' (Alerta de Altas Luces) con rutas de menu por marca.",
      },
      {
        tag: "Mejorado",
        text: "Enfasis en 'no existe la curva perfecta en forma de campana' — el histograma debe coincidir con el animo de la escena, no con un ideal abstracto.",
      },
    ],
  },
  {
    version: "1.7.0",
    date: "Feb 2026",
    title_en: "Advanced Techniques: Shots, Calibration & Tether",
    title_es: "Tecnicas Avanzadas: Planos, Calibracion y Tether",
    highlights_en: [
      {
        tag: "New",
        text: "Types of Shots / Planos Fotograficos — 8 standard shot types (extreme close-up to extreme wide) with diagram + photo example + when-to-use tips. Bilingual + downloadable PDF reference sheet.",
      },
      {
        tag: "New",
        text: "Monitor Calibration page — why calibration matters, color spaces (sRGB / Adobe RGB / DCI-P3), hardware vs. software methods, plus an interactive Live Monitor Test Suite (black level, white level, gamma, grayscale ramp, color uniformity, sharpness chart) that runs on your own screen.",
      },
      {
        tag: "New",
        text: "Tether Photography full guide — software comparison (Lightroom, Capture One, Canon EOS Utility, Nikon NX Tether, Sony Imaging Edge, Smart Shooter), cable types (USB-C, Tether Tools, JerkStopper), wireless tethering options, setup checklist, and troubleshooting.",
      },
      {
        tag: "Improved",
        text: "New 'Learn' menu in the Navbar groups all educational pages (Lighting, Golden Hour, Studio, Printing, Glossary, Shots, Calibration, Tether, Guide) for cleaner navigation on mobile.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Tipos de Planos / Planos Fotograficos — los 8 planos estandar (primerisimo plano a gran plano general) con diagrama + foto de ejemplo + cuando usar cada uno. Bilingue + hoja de referencia PDF descargable.",
      },
      {
        tag: "Nuevo",
        text: "Pagina de Calibracion del Monitor — por que importa, espacios de color (sRGB / Adobe RGB / DCI-P3), metodos por hardware vs software, mas un Suite de Pruebas en Vivo interactivo (nivel de negro, nivel de blanco, gamma, rampa de grises, uniformidad de color, carta de nitidez) que corre en tu propia pantalla.",
      },
      {
        tag: "Nuevo",
        text: "Guia completa de Fotografia con Tether — comparativa de software (Lightroom, Capture One, Canon EOS Utility, Nikon NX Tether, Sony Imaging Edge, Smart Shooter), tipos de cable (USB-C, Tether Tools, JerkStopper), tether inalambrico, lista de inicio rapido y resolucion de problemas.",
      },
      {
        tag: "Mejorado",
        text: "Nuevo menu 'Aprender' en la barra de navegacion agrupa todas las paginas educativas (Iluminacion, Hora Dorada, Estudio, Impresion, Glosario, Planos, Calibracion, Tether, Guia) para una navegacion mas limpia en movil.",
      },
    ],
  },
  {
    version: "1.6.0",
    date: "Apr 2026",
    title_en: "Golden Hour & Sun Times",
    title_es: "Hora Dorada y Tiempos Solares",
    highlights_en: [
      {
        tag: "New",
        text: "Plan your shoots around the best natural light. The new Golden Hour page shows sunrise, sunset, blue hour, and golden hour times for any location and date.",
      },
      {
        tag: "New",
        text: "Daily sun timeline with 13 phases (astronomical/nautical/civil twilights, golden hour, solar noon, etc.) and a quick photographer's tip for each phase.",
      },
      {
        tag: "New",
        text: "Sunrise/sunset compass bearings — know exactly where the sun will rise and set, so you can scout your shoot location.",
      },
      {
        tag: "New",
        text: "Moon info — moonrise, moonset, current phase, and illumination percentage. Great for night photography and astrophotography planning.",
      },
      {
        tag: "Improved",
        text: "Times are computed locally on your device — works offline once the page is loaded.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Planea tus sesiones alrededor de la mejor luz natural. La nueva pagina Hora Dorada muestra amaneceres, atardeceres, hora azul y hora dorada para cualquier ubicacion y fecha.",
      },
      {
        tag: "Nuevo",
        text: "Linea de tiempo solar con 13 fases (crepusculos astronomico/nautico/civil, hora dorada, mediodia solar, etc.) y un consejo rapido del fotografo para cada fase.",
      },
      {
        tag: "Nuevo",
        text: "Direccion compass del amanecer/atardecer — sabes exactamente donde saldra y se pondra el sol para explorar tu ubicacion.",
      },
      {
        tag: "Nuevo",
        text: "Informacion lunar — salida, puesta, fase actual y porcentaje de iluminacion. Ideal para fotografia nocturna y astrofotografia.",
      },
      {
        tag: "Mejorado",
        text: "Los tiempos se calculan localmente en tu dispositivo — funciona sin internet una vez cargada la pagina.",
      },
    ],
  },
  {
    version: "1.5.0",
    date: "Feb 2026",
    title_en: "Install as an app + Studio Guide boost",
    title_es: "Instalable como app + mejoras al Studio Guide",
    highlights_en: [
      {
        tag: "New",
        text: "Add DSLR Advisor to your phone's home screen — opens fullscreen like a native app, no browser bars.",
      },
      {
        tag: "New",
        text: "Printing & Output page: popular print sizes, DPI reference tables, and a sharp-print workflow checklist.",
      },
      {
        tag: "New",
        text: "Studio Setup Guide — complete build-out with small / mid-size floor plans, lighting kits, modifiers, and backgrounds.",
      },
      {
        tag: "New",
        text: "Budget Tier Calculator (up to $12,000, 43 items) — slide your budget and see exactly what to buy first.",
      },
      {
        tag: "New",
        text: "Shopping List that follows you across devices — check off gear you already own to refine the plan.",
      },
      {
        tag: "Improved",
        text: "Photography Tools PDF now exports the entire Quiz and Fundamentals sections, not just page one.",
      },
      {
        tag: "Improved",
        text: "Studio Guide equipment photos are now sharp, consistent product shots that actually match each card.",
      },
    ],
    highlights_es: [
      {
        tag: "Nuevo",
        text: "Anade DSLR Advisor a la pantalla de inicio de tu telefono — se abre a pantalla completa como una app nativa, sin barras del navegador.",
      },
      {
        tag: "Nuevo",
        text: "Pagina de Impresion y Salida: tamanos de impresion populares, tablas de referencia de DPI y un checklist de flujo para impresiones nitidas.",
      },
      {
        tag: "Nuevo",
        text: "Guia de Estudio Completa: planos de estudio pequeno y mediano, equipo de iluminacion, modificadores y fondos.",
      },
      {
        tag: "Nuevo",
        text: "Calculadora de Presupuesto por Nivel (hasta $12,000, 43 articulos): mueve tu presupuesto y ve exactamente que comprar primero.",
      },
      {
        tag: "Nuevo",
        text: "Lista de compras que te sigue entre dispositivos: marca el equipo que ya tienes y el plan se ajusta solo.",
      },
      {
        tag: "Mejorado",
        text: "El PDF de Herramientas de Fotografia ahora exporta todo el Quiz y la seccion de Fundamentos, no solo la primera pagina.",
      },
      {
        tag: "Mejorado",
        text: "Las fotos de equipo en la Guia de Estudio ahora son tomas limpias y consistentes que coinciden con cada tarjeta.",
      },
    ],
  },
];

export const WHATS_NEW_SEEN_KEY = "whats-new-seen-version";
export const OPEN_WHATS_NEW_EVENT = "open-whats-new";
